import { Link, useSearchParams } from "react-router-dom"
import { Newspaper, Calendar, ArrowRight, BookOpen, Search } from "lucide-react"
import { SiteShell } from "../../components/site/SiteShell"
import { PageHero } from "../../components/site/PageHero"
import { Card, CardContent } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import { formatDate } from "../../lib/constants"
import { usePublicNews } from "../../hooks/usePublic"

export default function News() {
  const [params, setParams] = useSearchParams()
  const filter = params.get("type") || "all"
  const { data, isLoading } = usePublicNews(50)

  const all = data?.news || []
  const filtered = all.filter(n => {
    if (filter === "all") return true
    if (filter === "news") return n.category !== "EVENT"
    if (filter === "events") return n.category === "EVENT"
    return true
  })

  const featured = filtered[0]
  const rest = filtered.slice(1)

  function setFilter(value: string) {
    if (value === "all") {
      params.delete("type")
    } else {
      params.set("type", value)
    }
    setParams(params, { replace: true })
  }

  const tabs = [
    { key: "all", label: "All" },
    { key: "news", label: "News" },
    { key: "events", label: "Events" },
  ]

  return (
    <SiteShell>
      <PageHero
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "News & Events" }]}
        subtitle="Stay Updated"
        title="News, Events & Announcements"
        description="Read the latest stories from across the College — academic milestones, faculty achievements, upcoming events, and important announcements for students, staff and the wider community."
      />

      {/* Filter tabs */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-1 border-b border-ink-200 dark:border-ink-800 overflow-x-auto">
            {tabs.map(t => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`px-5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  filter === t.key
                    ? "border-brand-600 text-brand-700 dark:text-brand-400"
                    : "border-transparent text-ink-500 hover:text-ink-800 dark:hover:text-ink-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured + grid */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid gap-6">
              {[1, 2, 3].map(i => (
                <Card key={i} className="border-0 shadow-md">
                  <CardContent className="pt-6">
                    <div className="animate-pulse flex gap-6">
                      <div className="w-48 h-32 bg-ink-200 dark:bg-ink-800 rounded-lg" />
                      <div className="flex-1 space-y-3">
                        <div className="h-4 bg-ink-200 dark:bg-ink-800 rounded w-1/4" />
                        <div className="h-6 bg-ink-200 dark:bg-ink-800 rounded w-3/4" />
                        <div className="h-4 bg-ink-200 dark:bg-ink-800 rounded w-full" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <Card className="border-0 shadow-md">
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-ink-100 dark:bg-ink-800 grid place-items-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-ink-400" />
                </div>
                <h3 className="font-display font-bold text-lg text-ink-900 dark:text-ink-100 mb-2">No articles found</h3>
                <p className="text-sm text-ink-500 dark:text-ink-400 max-w-md mx-auto">
                  There are no articles matching this filter at the moment. Check back soon or browse another category.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Link to={`/news/${featured.slug}`} className="block mb-10">
                  <Card className="card-lift border-0 shadow-lg overflow-hidden group">
                    <div className="grid lg:grid-cols-2 gap-0">
                      <div className="aspect-[16/10] lg:aspect-auto overflow-hidden bg-ink-100 dark:bg-ink-800">
                        {featured.image_url ? (
                          <img src={featured.image_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full grid place-items-center bg-gradient-to-br from-brand-100 to-gold-100 dark:from-brand-900/40 dark:to-gold-900/40">
                            <Newspaper className="w-16 h-16 text-brand-400 dark:text-brand-500" />
                          </div>
                        )}
                      </div>
                      <CardContent className="pt-6 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant={featured.category === "EVENT" ? "warning" : "success"}>{featured.category}</Badge>
                          <Badge variant="outline">Featured</Badge>
                          <span className="text-xs text-ink-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {formatDate(featured.published_at)}
                          </span>
                        </div>
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 dark:text-ink-100 leading-tight mb-3 group-hover:text-brand-700 dark:group-hover:text-brand-400 transition-colors">
                          {featured.title}
                        </h2>
                        <p className="text-sm text-ink-600 dark:text-ink-400 leading-relaxed mb-4 line-clamp-3">{featured.excerpt}</p>
                        {featured.author && (
                          <div className="text-xs text-ink-500 dark:text-ink-400 mb-4">By {featured.author}</div>
                        )}
                        <div className="text-sm font-semibold text-brand-700 dark:text-brand-400 inline-flex items-center gap-1">
                          Read full article <ArrowRight className="w-4 h-4" />
                        </div>
                      </CardContent>
                    </div>
                  </Card>
                </Link>
              )}

              {/* Grid */}
              {rest.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map(n => (
                    <Link key={n.id} to={`/news/${n.slug}`}>
                      <Card className="card-lift border-0 shadow-md overflow-hidden h-full group">
                        <div className="aspect-[16/10] overflow-hidden bg-ink-100 dark:bg-ink-800">
                          {n.image_url ? (
                            <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full grid place-items-center bg-gradient-to-br from-brand-100 to-gold-100 dark:from-brand-900/40 dark:to-gold-900/40">
                              <BookOpen className="w-10 h-10 text-brand-400 dark:text-brand-500" />
                            </div>
                          )}
                        </div>
                        <CardContent className="pt-5">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={n.category === "EVENT" ? "warning" : "success"}>{n.category}</Badge>
                            <span className="text-[11px] text-ink-400 flex items-center gap-1">
                              <Calendar className="w-3 h-3" /> {formatDate(n.published_at)}
                            </span>
                          </div>
                          <h3 className="font-display font-bold text-base text-ink-900 dark:text-ink-100 leading-tight mb-2 line-clamp-2 group-hover:text-brand-700 dark:group-hover:text-brand-400 transition-colors">
                            {n.title}
                          </h3>
                          <p className="text-sm text-ink-600 dark:text-ink-400 line-clamp-2 mb-3">{n.excerpt}</p>
                          <div className="text-xs font-semibold text-brand-700 dark:text-brand-400 inline-flex items-center gap-1">
                            Read more <ArrowRight className="w-3 h-3" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-950 dark:bg-ink-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots opacity-20" />
        <div className="container mx-auto px-4 relative text-center max-w-3xl mx-auto">
          <h2 className="font-display text-3xl font-bold mb-4">Never Miss an Update</h2>
          <p className="text-white/75 mb-8">
            Bookmark this page or follow us on social media to receive the latest news, event invitations and announcements from the College.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact">
              <Button size="lg" className="bg-gold-500 hover:bg-gold-600 text-ink-950">Subscribe</Button>
            </Link>
            <Link to="/downloads">
              <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/10">Browse Downloads</Button>
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
